import {Document, Page, Text, View, StyleSheet, Image} from '@react-pdf/renderer';
import {MarkdownPDF} from './MarkdownPDF';
import {Form, FormSubmission} from "crqlar-react-common/dist/models/forms/form";
import {Media} from "crqlar-react-common/dist/models/common/media.model";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {getScaledUrl, MediaSize} from "crqlar-react-common/dist/utils/media.util";
import {useTranslation} from "react-i18next";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: 'Helvetica'
  },
  header: {
    marginBottom: 20
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1a1a1a'
  },
  description: {
    fontSize: 11,
    color: '#666',
    marginBottom: 10
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    marginVertical: 15
  },
  metadataContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    marginBottom: 20
  },
  metadataItem: {
    flex: 1,
    minWidth: '45%'
  },
  metadataLabel: {
    fontSize: 9,
    color: '#999',
    textTransform: 'uppercase',
    marginBottom: 4
  },
  metadataValue: {
    fontSize: 11,
    color: '#333'
  },
  questionContainer: {
    marginBottom: 20,
    breakInside: 'avoid'
  },
  questionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#2196f3',
    marginBottom: 8
  },
  answerText: {
    fontSize: 11,
    color: '#333',
    lineHeight: 1.5
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4
  },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0'
  },
  chipSuccess: {
    backgroundColor: '#e8f5e9',
    borderColor: '#4caf50'
  },
  chipText: {
    fontSize: 9,
    color: '#666'
  },
  chipTextSuccess: {
    color: '#2e7d32'
  },
  mediaContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 8
  },
  mediaItem: {
    width: 200,
    marginBottom: 10
  },
  mediaThumbnail: {
    width: 200,
    height: 150,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    objectFit: 'contain'
  },
  mediaName: {
    fontSize: 8,
    color: '#666',
    marginTop: 4
  },
  footer: {
    marginTop: 30,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0'
  },
  footerText: {
    fontSize: 9,
    color: '#999'
  }
});

interface SubmissionPDFContentProps {
  submission: FormSubmission;
  form?: Form;
}

export function SubmissionPDFContent({submission, form}: SubmissionPDFContentProps) {
  const {t} = useTranslation();

  // Helper function to check if a value is a Media object
  const isMedia = (value: any): value is Media => {
    return value && typeof value === 'object' && 'mediaURI' in value;
  };

  // Helper function to check if a value is an array of Media objects
  const isMediaArray = (value: any): value is Media[] => {
    return Array.isArray(value) && value.length > 0 && isMedia(value[0]);
  };

  // Helper function to get question title by ID from form data
  const getQuestionTitle = (questionId: string): string => {
    if (!form?.questions) return questionId;
    const question = form.questions.find(q => q.id === questionId);
    return question?.title || questionId;
  };

  // Format submission date
  const submittedDate = formatDateDMYHM(submission.insertDate)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{form?.title || t('Form Submission')}</Text>
          {form?.description && (
            <Text style={styles.description}>{form.description}</Text>
          )}
          <View style={styles.divider} />
          <View style={styles.metadataContainer}>
            <View style={styles.metadataItem}>
              <Text style={styles.metadataLabel}>{t('Submitted On')}</Text>
              <Text style={styles.metadataValue}>{submittedDate}</Text>
            </View>
            {submission.email && (
              <View style={styles.metadataItem}>
                <Text style={styles.metadataLabel}>{t('Email')}</Text>
                <Text style={styles.metadataValue}>{submission.email}</Text>
              </View>
            )}
            {submission.phone && (
              <View style={styles.metadataItem}>
                <Text style={styles.metadataLabel}>{t('Phone')}</Text>
                <Text style={styles.metadataValue}>{submission.phone}</Text>
              </View>
            )}
          </View>
        </View>

        <View style={styles.divider} />

        {/* Questions and Answers */}
        {submission.answers?.map((answer, index) => {
          const questionTitle = answer.questionId
            ? getQuestionTitle(answer.questionId)
            : answer.questionTitle;

          return (
            <View key={index} style={styles.questionContainer}>
              <Text style={styles.questionTitle}>{questionTitle}</Text>
              {answer.questionDescription && (
                <MarkdownPDF style={styles.description}>
                  {answer.questionDescription}
                </MarkdownPDF>
              )}

              {/* Handle different answer types */}
              {isMedia(answer.answer) ? (
                <View style={styles.mediaContainer}>
                  <View style={styles.mediaItem}>
                    <Image
                      src={getScaledUrl(answer.answer as Media, MediaSize.LARGE_600, MediaSize.LARGE_600)}
                      style={styles.mediaThumbnail}
                    />
                  </View>
                </View>
              ) : isMediaArray(answer.answer) ? (
                <View style={styles.mediaContainer}>
                  {(answer.answer as Media[]).map((media, idx) => (
                    <View key={idx} style={styles.mediaItem}>
                      <Image
                        src={getScaledUrl(answer.answer as Media, MediaSize.LARGE_600, MediaSize.LARGE_600)}
                        style={styles.mediaThumbnail}
                      />
                    </View>
                  ))}
                </View>
              ) : Array.isArray(answer.answer) ? (
                <View style={styles.chipContainer}>
                  {answer.answer.map((value, idx) => (
                    <View key={idx} style={styles.chip}>
                      <Text style={styles.chipText}>{String(value)}</Text>
                    </View>
                  ))}
                </View>
              ) : typeof answer.answer === 'boolean' ? (
                <View style={styles.chipContainer}>
                  <View style={answer.answer ? [styles.chip, styles.chipSuccess] : styles.chip}>
                    <Text style={answer.answer ? [styles.chipText, styles.chipTextSuccess] : styles.chipText}>
                      {answer.answer ? t('Yes') : t('No')}
                    </Text>
                  </View>
                </View>
              ) : (
                <Text style={styles.answerText}>{String(answer.answer)}</Text>
              )}
            </View>
          );
        })}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>{t('Submission ID:')} {submission.id}</Text>
        </View>
      </Page>
    </Document>
  );
}
